import React from 'react';
import { Link } from 'react-router-dom';

const gradientBg = "bg-gradient-to-br from-[#0f2027] via-[#2c5364] to-[#1a2980]";
const glassCard = "bg-white/5 backdrop-blur-lg rounded-2xl shadow-xl border border-white/10 p-6";

const principles = [
  {
    title: "Décentralisation",
    description: "Aucune entité unique ne contrôle Jupipotes. Le pouvoir est distribué entre tous les membres.",
    icon: "🌐",
    details: [
      "Gouvernance par DAO",
      "Pas de point de défaillance unique",
      "Données distribuées",
      "Transparence totale"
    ]
  },
  {
    title: "Collaboration",
    description: "Nous croyons que les meilleures solutions émergent de la collaboration collective.",
    icon: "🤝",
    details: [
      "Open source",
      "Contributions communautaires",
      "Partage des connaissances",
      "Entraide active"
    ]
  },
  {
    title: "Innovation",
    description: "Nous repoussons constamment les limites du possible dans le Web3 social.",
    icon: "🚀",
    details: [
      "Technologies de pointe",
      "Expérimentation continue",
      "Nouvelles fonctionnalités",
      "Vision long terme"
    ]
  },
  {
    title: "Équité",
    description: "Chaque membre a les mêmes droits et opportunités de participer et d'influencer.",
    icon: "⚖️",
    details: [
      "Un token = Un vote",
      "Récompenses basées sur la contribution",
      "Accès égal aux ressources",
      "Modération juste"
    ]
  }
];

const tokenomics = {
  totalSupply: "À déterminer",
  distribution: [
    { label: "Communauté & Récompenses", percentage: 40, color: "cyan" },
    { label: "Trésorerie DAO", percentage: 25, color: "emerald" },
    { label: "Équipe & Contributeurs", percentage: 20, color: "blue" },
    { label: "Liquidité & Écosystème", percentage: 15, color: "purple" }
  ],
  note: "La tokenisation est prévue pour 2026. Ces chiffres sont indicatifs."
};

const useCases = [
  {
    title: "Gouvernance",
    description: "Participez aux décisions qui façonnent l'avenir de Jupipotes (à venir)",
    icon: "🗳️"
  },
  {
    title: "Récompenses",
    description: "Système de reconnaissance pour les créateurs de contenu (en développement)",
    icon: "💰"
  },
  {
    title: "Badges Communautaires",
    description: "Gagnez des badges en fonction de votre engagement et contributions",
    icon: "🎨"
  },
  {
    title: "Accès Créateurs",
    description: "Outils avancés et analytics pour les créateurs de contenu actifs",
    icon: "⭐"
  },
  {
    title: "Programme Ambassadeurs",
    description: "Rejoignez l'équipe des ambassadeurs Jupipotes (bientôt disponible)",
    icon: "🚀"
  },
  {
    title: "Support Créateurs",
    description: "Soutenez vos créateurs préférés (fonctionnalité future)",
    icon: "💎"
  }
];

const roadmap = [
  {
    phase: "Phase 1 - Fondations (Q1-Q2 2025)",
    status: "Complété",
    items: [
      "Lancement de la plateforme beta",
      "Système de publications sociales",
      "Messagerie privée temps réel",
      "Profils utilisateurs et paramètres"
    ]
  },
  {
    phase: "Phase 2 - Croissance (Q3-Q4 2025)",
    status: "En cours",
    items: [
      "Optimisation performance et UX",
      "Intégration médias (vidéo, images)",
      "Application mobile Progressive Web App",
      "Système de modération avancé"
    ]
  },
  {
    phase: "Phase 3 - Engagement (2026 Q1-Q2)",
    status: "À venir",
    items: [
      "Programme ambassadeurs communautaires",
      "Analytics et dashboard créateurs",
      "Système de badges et récompenses",
      "Programme de parrainage"
    ]
  },
  {
    phase: "Phase 4 - Écosystème Web3 (2026 Q3+)",
    status: "Planifié",
    items: [
      "Tokenisation (JUP token)",
      "DAO et gouvernance décentralisée",
      "Marketplace NFT intégrée",
      "API publique pour développeurs"
    ]
  }
];

const stats = [
  { label: "Membres actifs", value: "1,200+", icon: "👥" },
  { label: "Publications", value: "8,500+", icon: "📝" },
  { label: "Interactions", value: "45K+", icon: "⭐" },
  { label: "Messages échangés", value: "25K+", icon: "💬" }
];

const AboutJUP: React.FC = () => {
  return (
    <div className={`min-h-screen w-full ${gradientBg} py-8 px-4`}>
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-block mb-6">
            <img 
              src="https://support.jup.ag/hc/article_attachments/20007956605852" 
              alt="J.U.P Logo" 
              className="w-40 h-40 mx-auto rounded-full border-4 border-cyan-400 shadow-2xl animate-pulse"
            />
          </div>
          <h1 className="text-6xl font-extrabold text-emerald-100 mb-4">
            Qu'est-ce que le J.U.P ?
          </h1>
          <p className="text-2xl text-cyan-300 font-semibold mb-6">
            Jupiter United Planet
          </p>
          <p className="text-xl text-emerald-200 max-w-4xl mx-auto leading-relaxed">
            Le J.U.P n'est pas qu'un simple token ou une plateforme. C'est un <strong className="text-cyan-300">mouvement</strong>, 
            une <strong className="text-emerald-300">communauté</strong> et une <strong className="text-cyan-300">vision</strong> 
            pour l'avenir des réseaux sociaux décentralisés.
          </p>
        </div>

        {/* Mission Statement */}
        <section className={`${glassCard} mb-12`}>
          <h2 className="text-3xl font-bold text-cyan-300 mb-6 text-center">Notre Mission</h2>
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-xl text-emerald-200 leading-relaxed mb-6">
              Créer une plateforme sociale véritablement décentralisée où <strong>chaque voix compte</strong>, 
              où la <strong>créativité est récompensée</strong> équitablement, et où la <strong>communauté</strong> 
              décide de son propre avenir.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-4xl">
              <span>🌍</span>
              <span>🤝</span>
              <span>💎</span>
              <span>🚀</span>
              <span>⚡</span>
            </div>
          </div>
        </section>

        {/* Principes Fondamentaux */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-cyan-300 mb-8 text-center">
            Nos Principes Fondamentaux
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {principles.map((principle, idx) => (
              <div key={idx} className={`${glassCard} hover:border-cyan-400/50 transition-all duration-300`}>
                <div className="text-5xl mb-4">{principle.icon}</div>
                <h3 className="text-2xl font-bold text-emerald-100 mb-3">{principle.title}</h3>
                <p className="text-emerald-200 mb-4 text-lg">{principle.description}</p>
                <ul className="space-y-2">
                  {principle.details.map((detail, detailIdx) => (
                    <li key={detailIdx} className="flex items-center gap-2 text-cyan-300">
                      <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Stats */}
        <section className="mb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
              <div key={idx} className={`${glassCard} text-center hover:scale-105 transition-transform duration-300`}>
                <div className="text-4xl mb-3">{stat.icon}</div>
                <div className="text-3xl font-bold text-cyan-300 mb-2">{stat.value}</div>
                <div className="text-emerald-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Token JUP - Plan futur */}
        <section className={`${glassCard} mb-12 relative`}>
          <div className="absolute top-4 right-4 bg-purple-500/20 text-purple-300 px-4 py-2 rounded-full text-sm font-bold border border-purple-500/50">
            📅 Prévu 2026
          </div>
          <h2 className="text-3xl font-bold text-cyan-300 mb-8 flex items-center gap-3">
            <span className="text-4xl">💎</span>
            Le Token JUP (Futur)
          </h2>
          <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-4 mb-6">
            <p className="text-cyan-300 text-center">
              ℹ️ La tokenisation est prévue pour 2026. Voici un aperçu de la vision à long terme.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold text-emerald-100 mb-4">Distribution Prévue</h3>
              <div className="space-y-4">
                {tokenomics.distribution.map((item, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between mb-2">
                      <span className="text-emerald-200">{item.label}</span>
                      <span className="text-cyan-300 font-bold">{item.percentage}%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-3">
                      <div 
                        className={`bg-gradient-to-r from-${item.color}-400 to-${item.color}-600 h-3 rounded-full transition-all duration-1000 opacity-50`}
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-purple-900/20 rounded-lg border border-purple-700/30">
                <p className="text-purple-300 font-semibold">Supply Total (Indicatif)</p>
                <p className="text-2xl font-bold text-emerald-100">{tokenomics.totalSupply}</p>
                <p className="text-xs text-purple-200 mt-2">Ces valeurs seront définies par la communauté</p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold text-emerald-100 mb-4">Fonctionnalités & Usages</h3>
              <div className="grid grid-cols-2 gap-4">
                {useCases.map((useCase, idx) => (
                  <div key={idx} className="bg-emerald-900/20 rounded-lg p-4 border border-emerald-700/30 hover:border-emerald-500/50 transition-all duration-300">
                    <div className="text-3xl mb-2">{useCase.icon}</div>
                    <h4 className="text-emerald-100 font-semibold mb-1 text-sm">{useCase.title}</h4>
                    <p className="text-emerald-200 text-xs">{useCase.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Roadmap */}
        <section className={`${glassCard} mb-12`}>
          <h2 className="text-3xl font-bold text-cyan-300 mb-8 flex items-center gap-3">
            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            Roadmap
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {roadmap.map((phase, idx) => (
              <div key={idx} className="relative">
                <div className={`bg-gradient-to-br ${
                  phase.status === 'Complété' ? 'from-emerald-900/40 to-green-900/40 border-green-500/50' :
                  phase.status === 'En cours' ? 'from-cyan-900/40 to-blue-900/40 border-cyan-500/50' :
                  'from-purple-900/40 to-indigo-900/40 border-purple-500/30'
                } rounded-xl p-6 border-2 h-full`}>
                  <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-4 ${
                    phase.status === 'Complété' ? 'bg-green-500/20 text-green-300' :
                    phase.status === 'En cours' ? 'bg-cyan-500/20 text-cyan-300' :
                    'bg-purple-500/20 text-purple-300'
                  }`}>
                    {phase.status}
                  </div>
                  <h3 className="text-lg font-bold text-emerald-100 mb-4">{phase.phase}</h3>
                  <ul className="space-y-2">
                    {phase.items.map((item, itemIdx) => (
                      <li key={itemIdx} className="flex items-start gap-2 text-sm text-emerald-200">
                        <span className="text-cyan-400 mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Philosophy */}
        <section className={`${glassCard} mb-12`}>
          <h2 className="text-3xl font-bold text-cyan-300 mb-6">Pourquoi le J.U.P est Différent</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-4">
              <div className="text-4xl">🔓</div>
              <h3 className="text-xl font-bold text-emerald-100">Open & Transparent</h3>
              <p className="text-emerald-200">
                Tout est open source. Toutes les décisions sont publiques. Pas de secrets, pas de manipulation.
              </p>
            </div>
            <div className="space-y-4">
              <div className="text-4xl">💪</div>
              <h3 className="text-xl font-bold text-emerald-100">Community First</h3>
              <p className="text-emerald-200">
                La communauté passe avant tout. Chaque décision majeure est votée par les membres.
              </p>
            </div>
            <div className="space-y-4">
              <div className="text-4xl">🌱</div>
              <h3 className="text-xl font-bold text-emerald-100">Croissance Durable</h3>
              <p className="text-emerald-200">
                Nous construisons pour le long terme, pas pour le profit rapide. Croissance organique et durable.
              </p>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <div className={`${glassCard} text-center`}>
          <h3 className="text-3xl font-bold text-emerald-100 mb-4">
            Prêt à Faire Partie de l'Histoire ?
          </h3>
          <p className="text-xl text-emerald-200 mb-8 max-w-2xl mx-auto">
            Le J.U.P n'est pas juste une plateforme, c'est un mouvement. Rejoignez-nous et contribuez à construire 
            l'avenir des réseaux sociaux décentralisés.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/communities"
              className="px-8 py-4 bg-cyan-400/20 text-cyan-200 font-bold rounded-xl hover:bg-cyan-400/40 transition-all duration-300 flex items-center gap-2"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Commencer Maintenant
            </Link>
            <Link
              to="/community-dao"
              className="px-8 py-4 bg-emerald-700/20 text-emerald-200 font-bold rounded-xl hover:bg-emerald-700/40 transition-all duration-300 flex items-center gap-2"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Découvrir le DAO
            </Link>
          </div>
        </div>

        {/* Retour */}
        <div className="mt-8 text-center">
          <Link
            to="/JUPResources"
            className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour aux ressources
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AboutJUP;
